/**
 * Format dates consistently across the app (British format).
 * @param {string|Date} dateString - date to format
 * @param {string} style - 'full' | 'short' | 'compact' | 'relative' | 'datetime'
 * @returns {string}
 */
export function formatDate(dateString, style = 'full') {
  if (!dateString) return '';
  // Normalise format before parsing
  let normalised = dateString;
  if (typeof dateString === 'string') {
    if (/^\d{1,2}\.\d{1,2}\.\d{2,4}$/.test(dateString)) {
      // "dd.mm.yyyy" or "dd.mm.yy" — reverse to ISO
      normalised = dateString.split('.').reverse().join('-');
    } else if (/^\d{4}-\d{2}-\d{2} /.test(dateString)) {
      // PostgreSQL timestamp "2026-04-10 13:34:20.20549+00" — needs T separator
      // and colon in timezone offset (+00 → +00:00) for V8 to parse correctly
      normalised = dateString.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
    }
  }
  const d = dateString instanceof Date ? dateString : new Date(normalised);
  if (isNaN(d.getTime())) return String(dateString);

  if (style === 'full') {
    // "9 Apr 2026"
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (style === 'weekday') {
    // "Thu, 9 Apr 2026"
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (style === 'short') {
    // "09/04/2026"
    return d.toLocaleDateString('en-GB');
  }
  if (style === 'compact') {
    // "9 Apr" (no year)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  if (style === 'datetime') {
    // "9 Apr 2026 14:30"
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (style === 'relative') {
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    // Fall back to full format
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
